import { useState, useEffect } from "react";
import { Plus, X, Edit3 } from "lucide-react";
import { PlanItemsEditor } from "@/components/TemplatesTab";

function safeUUID() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try { return crypto.randomUUID(); } catch (_) {}
  }
  return "id-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
}

/**
 * PlanDaysEditor — day tabs + nested PlanItemsEditor per day.
 * days: [{ id, name, order, items[] }]
 * setDays: setter for parent's `days` state.
 *
 * Days are sequential (Giorno 1, 2, 3...); no drag-to-reorder.
 * Add day appends at end; remove day removes and re-indexes names if using default names.
 */
export default function PlanDaysEditor({ days, setDays, exercises }) {
  // Ensure at least one day exists
  useEffect(() => {
    if (!days || days.length === 0) {
      setDays([{ id: safeUUID(), name: "Giorno 1", order: 0, items: [] }]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [activeIdx, setActiveIdx] = useState(0);
  const [renameIdx, setRenameIdx] = useState(-1);
  const [renameVal, setRenameVal] = useState("");

  if (!days || days.length === 0) return null;

  const active = days[activeIdx] || days[0];

  const setActiveItems = (newItems) => {
    setDays(days.map((d, i) => i === activeIdx ? { ...d, items: newItems } : d));
  };

  const addDay = () => {
    const nextIdx = days.length;
    setDays([
      ...days,
      { id: safeUUID(), name: `Giorno ${nextIdx + 1}`, order: nextIdx, items: [] },
    ]);
    setActiveIdx(nextIdx);
  };

  const removeDay = (idx) => {
    if (days.length === 1) return; // keep at least 1 day
    if (!window.confirm(`Eliminare "${days[idx].name}" e tutti i suoi esercizi?`)) return;
    const next = days.filter((_, i) => i !== idx).map((d, i) => ({ ...d, order: i }));
    setDays(next);
    setActiveIdx(Math.max(0, Math.min(activeIdx, next.length - 1)));
  };

  const startRename = (idx) => {
    setRenameIdx(idx);
    setRenameVal(days[idx].name);
  };
  const confirmRename = () => {
    if (renameIdx < 0) return;
    setDays(days.map((d, i) => i === renameIdx ? { ...d, name: renameVal.trim() || d.name } : d));
    setRenameIdx(-1);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="plan-days-tabs">
        {days.map((d, i) => (
          <div key={d.id} className="flex items-center">
            <button
              type="button"
              data-testid={`day-tab-${i}`}
              onClick={() => setActiveIdx(i)}
              className={`px-3 py-1.5 font-heading uppercase tracking-widest text-xs border ${i === activeIdx ? "bg-[#A855F7] border-[#A855F7] text-white" : "border-white/15 text-white/70 hover:border-[#A855F7]"}`}>
              {d.name}
              <span className="text-[10px] ml-2 opacity-70">({(d.items || []).length})</span>
            </button>
            {i === activeIdx && (
              <>
                <button type="button" onClick={() => startRename(i)} data-testid={`day-rename-${i}`}
                  className="ml-1 p-1 text-white/50 hover:text-white" title="Rinomina">
                  <Edit3 size={12} />
                </button>
                {days.length > 1 && (
                  <button type="button" onClick={() => removeDay(i)} data-testid={`day-remove-${i}`}
                    className="ml-1 p-1 text-[#FF3366]/60 hover:text-[#FF3366]" title="Elimina giorno">
                    <X size={12} />
                  </button>
                )}
              </>
            )}
          </div>
        ))}
        <button type="button" onClick={addDay} data-testid="add-day-btn"
          className="px-2 py-1.5 font-heading uppercase tracking-widest text-xs border border-dashed border-white/30 text-white/60 hover:border-[#A855F7] hover:text-white flex items-center gap-1">
          <Plus size={12} /> Aggiungi giorno
        </button>
      </div>

      {renameIdx >= 0 && (
        <div className="mb-3 flex items-center gap-2">
          <input value={renameVal} onChange={e => setRenameVal(e.target.value)}
            data-testid="day-rename-input"
            className="fld2 flex-1" placeholder={`Giorno ${renameIdx + 1}`} />
          <button type="button" data-testid="day-rename-confirm" onClick={confirmRename} className="btn-primary !py-1.5">OK</button>
          <button type="button" onClick={() => setRenameIdx(-1)} className="btn-ghost !py-1.5">Annulla</button>
        </div>
      )}

      <PlanItemsEditor
        items={active.items || []}
        setItems={setActiveItems}
        exercises={exercises}
      />
    </div>
  );
}
